import {Inject, Injectable, Injector, LOCALE_ID} from '@angular/core';
import {forkJoin, map, Observable, of, shareReplay, switchMap, tap} from 'rxjs';
import {BaseService} from './base.service';
import {Dictionary, DictionaryStatistics, Language, Section} from '../models';

@Injectable({
  providedIn: 'root'
})
export class DictionaryService extends BaseService {
  private dictCache: Map<number, Observable<Dictionary[]>> = new Map();
  private dictAvailableCache: Map<number, Observable<Dictionary[]>> = new Map();

  // Cache maps keyed by dictionary ID for endpoints that use dictId.
  private languagesCache: Map<number, Observable<Language[]>> = new Map();
  private allLanguagesCache: Map<number, Observable<Language[]>> = new Map();
  private dictionaryDetailCache: Map<number, Observable<Dictionary>> = new Map();
  private sectionsCache: Map<number, Observable<Section[]>> = new Map();
  private allSectionsCache: Map<number, Observable<Section[]>> = new Map();

  private currentLocale: string;

  constructor(injector: Injector, @Inject(LOCALE_ID) localeId: string) {
    super(injector);
    this.currentLocale = localeId.split('-')[0];
  }

  /**
   * Returns the language for specified code in the language array.
   * @param langs Language array
   * @param langCode Language code.
   */
  public getLanguage(langs: Language[], langCode?: string): Language | undefined {
    return langs.find((l) => l.code === langCode) || undefined;
  }

  /**
   * Returns the language name for specified language code in the language array.
   * @param langs Language array
   * @param langCode Language code.
   */
  public getLanguageName(langs: Language[], langCode?: string): string {
    return langs.find((l) => l.code === langCode)?.name || '';
  }

  /**
   * Gets languages for a given array of dictionary IDs.
   * Results are cached per dictId.
   */
  public getLanguages(dictIds: number[] | undefined): Observable<Language[]> {
    if (!dictIds) {
      return of([]);
    }
    if (dictIds.length === 0) {
      return this.getDictionaries(true).pipe(
        map(dicts => dicts.map(dict => dict.id)),
        switchMap(allDictIds => allDictIds.length === 0 ? of([]) : this.getLanguages(allDictIds))
      );
    }
    const observables: Observable<Language[]>[] = [];

    for (const id of dictIds) {
      if (!this.languagesCache.has(id)) {
        const obs = this.http.get<Language[]>(this.url(`/dictionaries/${id}/languages`), {
            headers: {'Accept-Language': this.currentLocale}
          }).pipe(shareReplay(1));
        this.languagesCache.set(id, obs);
      }
      observables.push(this.languagesCache.get(id)!);
    }

    return forkJoin(observables).pipe(
      map((languageSets: Language[][]) => {
        if (languageSets.length === 0) {
          return [];
        }

        const result = [...languageSets[0]];
        const langCodeSet = new Set(result.map(lang => lang.code));

        for (let i = 1; i < languageSets.length; i++) {
          for (const lang of languageSets[i]) {
            if (!langCodeSet.has(lang.code)) {
              result.push(lang);
              langCodeSet.add(lang.code);
            }
          }
        }
        return result;
      }));
  }

  /**
   * Gets all languages (including disabled or not normally visible) for a given dictionary.
   * Results are cached per dictId.
   */
  public getAllLanguages(dictId: number): Observable<Language[]> {
    if (!this.allLanguagesCache.has(dictId)) {
      const obs = this.http.get<Language[]>(this.url(`/dictionaries/${dictId}/languages?all=true`), {
        headers: {'Accept-Language': this.currentLocale}
      }).pipe(shareReplay(1));
      this.allLanguagesCache.set(dictId, obs);
    }
    return this.allLanguagesCache.get(dictId)!;
  }

  /**
   * Returns the dictionary definition for the given dictId.
   * Result is cached per dictId.
   */
  public getDictionary(dictId: number): Observable<Dictionary> {
    if (!this.dictionaryDetailCache.has(dictId)) {
      const obs = this.http.get<Dictionary>(this.url(`/dictionaries/${dictId}`))
        .pipe(shareReplay(1));
      this.dictionaryDetailCache.set(dictId, obs);
    }
    return this.dictionaryDetailCache.get(dictId)!;
  }

  /**
   * Returns all dictionaries.
   * Result is cached until refreshDictionaries() is called.
   */
  public getDictionaries(onyAvailable: boolean = false): Observable<Dictionary[]> {
    const obs = onyAvailable ? this.dictAvailableCache : this.dictCache;
    if (!obs.has(1)) {
      let uri = '/dictionaries';
      if (onyAvailable) {
        uri += '?only-available=true';
      }
      const value = this.http.get<Dictionary[]>(this.url(uri)).pipe(shareReplay(1));
      obs.set(1, value)
    }
    return obs.get(1)!;
  }

  /**
   * Returns statistics for all dictionaries.
   */
  public getDictionaryStatistics(): Observable<DictionaryStatistics[]> {
    return this.http.get<DictionaryStatistics[]>(this.url('/dictionaries/statistics')).pipe(shareReplay(1));
  }

  /**
   * Returns statistics for one dictionary.
   */
  public getDictionaryStatistic(dictId: number): Observable<DictionaryStatistics | undefined> {
    return this.getDictionaryStatistics().pipe(
      map(stats => stats.find(stat => stat.id === dictId))
    );
  }

  /**
   * Performs reindexing of the entire dictionary.
   * This operation runs asynchronously on the server.
   */
  public reindexDictionary(dictId: number): Observable<void> {
    return this.http.post<void>(this.url(`/dictionaries/${dictId}/reindex`), {}).pipe(
      tap(() => this.clearCache())
    );
  }

  /**
   * Returns the sections for a given dictionary.
   * Result is cached per dictId.
   */
  public getSections(dictIds: number[] | undefined): Observable<Section[]> {
    if (!dictIds) return of([]);
    if (dictIds.length === 0) {
      return this.getDictionaries(true).pipe(
        map(dicts => dicts.map(dict => dict.id)),
        switchMap(allDictIds => allDictIds.length === 0 ? of([]) : this.getSections(allDictIds))
      );
    }

    const observables: Observable<Section[]>[] = [];

    for (const id of dictIds) {
      if (!this.sectionsCache.has(id)) {
        const obs = this.http.get<Section[]>(this.url(`/dictionaries/${id}/sections`))
          .pipe(shareReplay(1));
        this.sectionsCache.set(id, obs);
      }
      observables.push(this.sectionsCache.get(id)!);
    }

    return forkJoin(observables).pipe(
      map((sectionSets: Section[][]) => {
        if (sectionSets.length === 0) {
          return [];
        }

        const result = [...sectionSets[0]];
        const sectionNameSet = new Set(result.map(section => section.name));

        for (let i = 1; i < sectionSets.length; i++) {
          for (const section of sectionSets[i]) {
            if (!sectionNameSet.has(section.name)) {
              result.push(section);
              sectionNameSet.add(section.name);
            }
          }
        }
        return result;
      }));
  }

  /**
   * Returns all sections for a given dictionary.
   * Result is cached per dictId.
   */
  public getAllSections(dictId: number): Observable<Section[]> {
    if (!this.allSectionsCache.has(dictId)) {
      const obs = this.http.get<Section[]>(this.url(`/dictionaries/${dictId}/sections?all=true`))
        .pipe(shareReplay(1));
      this.allSectionsCache.set(dictId, obs);
    }
    return this.allSectionsCache.get(dictId)!;
  }

  /**
   * Updates a section.
   */
  updateSection(section: Section): Observable<any> {
    return this.http.put(this.url(`/dictionaries/${section.dictionaryId}/sections`), section);
  }

  /**
   * Refreshes dictionary and fetches the latest term data changes.
   * This operation runs asynchronously on the server.
   */
  public refreshDictionary(dictId: number): Observable<void> {
    return this.http.post<void>(this.url(`/dictionaries/${dictId}/refresh`), {});
  }

  /**
   * Refreshes the list of dictionaries.
   * On successful refresh, clears all cached values.
   */
  public refreshDictionaries(): Observable<any> {
    return this.http.post(this.url('/dictionaries/refresh'), {}).pipe(
      tap(() => this.clearCache())
    );
  }

  /**
   * Deletes the index for an entire dictionary.
   */
  public deleteIndex(dictId: number): Observable<any> {
    return this.http.post(this.url(`/dictionaries/${dictId}/delete-index`), {}).pipe(
      tap(() => this.clearCacheForDictionary(dictId))
    );
  }

  /**
   * Updates a language.
   */
  updateLanguage(language: Language) {
    return this.http.put(this.url(`/dictionaries/${language.dictionaryId}/languages`), language);
  }

  /**
   * Clears cached values related to a specific dictionary.
   */
  clearCacheForDictionary(dictId: number): void {
    this.dictCache.clear();
    this.dictAvailableCache.clear();
    this.languagesCache.delete(dictId);
    this.allLanguagesCache.delete(dictId);
    this.dictionaryDetailCache.delete(dictId);
    this.sectionsCache.delete(dictId);
    this.allSectionsCache.delete(dictId);
  }

  /**
   * Clears all cached values.
   */
  clearCache(): void {
    this.dictCache.clear();
    this.dictAvailableCache.clear();
    this.languagesCache.clear();
    this.allLanguagesCache.clear();
    this.dictionaryDetailCache.clear();
    this.sectionsCache.clear();
    this.allSectionsCache.clear();
  }
}
